import type {
  ChapterId,
  ClientContext,
  FlowChapter,
  FlowStep,
  OnboardingAnswers,
  PersonAnswer,
} from "./types";

export const CHAPTERS: readonly FlowChapter[] = [
  { id: "quick-start", number: 1, label: "Quick Start" },
  { id: "service-area", number: 2, label: "Service Area" },
  { id: "team-systems", number: 3, label: "Team & Systems" },
  { id: "lead-follow-up", number: 4, label: "Lead Follow-Up" },
  { id: "creative-library", number: 5, label: "Creative Library" },
  { id: "call-prep", number: 6, label: "Call Prep" },
] as const;

export const STEP_PREFIX = {
  reviewLocation: "review-location:",
  serviceArea: "service-area:",
} as const;

function step(
  id: string,
  chapter: ChapterId,
  label: string,
): FlowStep {
  return {
    id,
    chapter,
    section: CHAPTERS.find((item) => item.id === chapter)?.label ?? "Kickoff",
    label,
  };
}

export function selectedLocations(
  client: ClientContext,
  answers: OnboardingAnswers,
) {
  if (client.locations.length === 1) {
    return client.locations;
  }
  return client.locations.filter((location) =>
    answers.selectedLocationIds.includes(location.id),
  );
}

export function getFlow(
  client: ClientContext,
  answers: OnboardingAnswers,
): FlowStep[] {
  const locations = selectedLocations(client, answers);
  const flow: FlowStep[] = [step("welcome", "quick-start", "Welcome")];

  if (client.locations.length > 1) {
    flow.push(step("locations", "quick-start", "Confirm locations"));
  }

  for (const location of locations) {
    flow.push(
      step(
        `${STEP_PREFIX.reviewLocation}${location.id}`,
        "service-area",
        `${location.name} details`,
      ),
    );
  }

  if (locations.length > 1) {
    flow.push(
      step("location-budgets", "service-area", "Marketing budgets"),
      step("location-schedules", "service-area", "Sales schedules"),
    );
  }

  for (const location of locations) {
    flow.push(
      step(
        `${STEP_PREFIX.serviceArea}${location.id}`,
        "service-area",
        `${location.name} service ZIPs`,
      ),
    );
  }

  flow.push(
    step("sales-role", "team-systems", "Sales role"),
    step("quality-role", "team-systems", "Quality role"),
    step("email-platform", "team-systems", "Email platform"),
    step("fsm", "team-systems", "Field service software"),
  );

  if (answers.fsm === "servicetitan") {
    flow.push(step("marketing-pro", "team-systems", "Marketing Pro"));
  }

  flow.push(
    step("shared-inbox", "lead-follow-up", "Shared inbox"),
    step("lead-owner", "lead-follow-up", "Lead responder"),
    step("office-manager", "lead-follow-up", "Office manager"),
    step("lead-notifications", "lead-follow-up", "Notifications"),
    step("salespeople", "lead-follow-up", "Dedicated salespeople"),
    step("sales-manager", "lead-follow-up", "Sales manager"),
    step("media-sharing", "creative-library", "Share media"),
    step("meeting-prep", "call-prep", "Launch readiness"),
    step("review", "call-prep", "Review"),
  );

  if (answers.submitted) {
    flow.push(step("scheduler", "call-prep", "Choose a time"));
  }

  if (answers.meetingBooked) {
    flow.push(step("complete", "call-prep", "You’re all set"));
  }

  return flow;
}

export function getChapterProgress(
  flow: FlowStep[],
  currentStepId: string,
): {
  currentChapter: FlowChapter;
  segments: Array<FlowChapter & { fill: number; state: "complete" | "active" | "future" }>;
} {
  const currentStep = flow.find((item) => item.id === currentStepId) ?? flow[0];
  const currentChapter =
    CHAPTERS.find((chapter) => chapter.id === currentStep?.chapter) ?? CHAPTERS[0];
  const currentChapterIndex = CHAPTERS.findIndex(
    (chapter) => chapter.id === currentChapter.id,
  );
  const chapterSteps = flow.filter((item) => item.chapter === currentChapter.id);
  const stepIndex = Math.max(
    0,
    chapterSteps.findIndex((item) => item.id === currentStepId),
  );
  const activeFill =
    currentStepId === "complete"
      ? 100
      : Math.max(18, Math.round((stepIndex / Math.max(chapterSteps.length, 1)) * 100));

  return {
    currentChapter,
    segments: CHAPTERS.map((chapter, index) => ({
      ...chapter,
      fill: index < currentChapterIndex ? 100 : index === currentChapterIndex ? activeFill : 0,
      state:
        index < currentChapterIndex
          ? "complete"
          : index === currentChapterIndex
            ? "active"
            : "future",
    })),
  };
}

export function resolveResumableStep(
  client: ClientContext,
  answers: OnboardingAnswers,
  storedStep?: string | null,
): string {
  const flow = getFlow(client, answers);
  if (storedStep === "locations" && client.locations.length === 1) {
    return firstSelectedLocationStep(client, answers, STEP_PREFIX.reviewLocation);
  }
  if (storedStep === "location-budgets" && selectedLocations(client, answers).length <= 1) {
    return firstSelectedLocationStep(client, answers, STEP_PREFIX.serviceArea);
  }
  const storedStepIndex = storedStep
    ? flow.findIndex((step) => step.id === storedStep)
    : -1;
  const earliestIncompleteLocationStep = flow.findIndex((candidate) =>
    candidate.chapter === "service-area" &&
    getStepErrors(candidate.id, client, answers).length > 0
  );
  if (
    earliestIncompleteLocationStep >= 0 &&
    (storedStepIndex < 0 || storedStepIndex > earliestIncompleteLocationStep)
  ) {
    return flow[earliestIncompleteLocationStep].id;
  }
  if (storedStepIndex >= 0) return flow[storedStepIndex].id;

  if (storedStep?.startsWith("exclusions:")) {
    const completedLocationId = storedStep.slice("exclusions:".length);
    const locations = selectedLocations(client, answers);
    const completedIndex = locations.findIndex(
      (location) => location.id === completedLocationId,
    );
    const nextLocation = locations[completedIndex + 1];
    return nextLocation
      ? `${STEP_PREFIX.serviceArea}${nextLocation.id}`
      : "sales-role";
  }
  if (storedStep === "lead-intro") return "shared-inbox";
  if (storedStep === "lead-manager") return "office-manager";
  if (storedStep === "media-intro" || storedStep === "media-consent") {
    return "media-sharing";
  }

  return answers.submitted ? "scheduler" : "welcome";
}

function firstSelectedLocationStep(
  client: ClientContext,
  answers: OnboardingAnswers,
  prefix: (typeof STEP_PREFIX)[keyof typeof STEP_PREFIX],
): string {
  const firstLocation = selectedLocations(client, answers)[0];
  return firstLocation ? `${prefix}${firstLocation.id}` : "locations";
}

export function parseZipCodes(value: string): string[] {
  return Array.from(
    new Set(value.match(/\b\d{5}\b/g)?.map((zip) => zip.trim()) ?? []),
  );
}

export function targetZipCodes(
  detail: Pick<
    OnboardingAnswers["locationDetails"][string],
    "serviceZipCodes" | "serviceAreaExclusionMode" | "excludedZipCodes"
  >,
): string[] {
  const excluded = detail.serviceAreaExclusionMode === "exclude"
    ? new Set(parseZipCodes(detail.excludedZipCodes))
    : new Set<string>();
  return parseZipCodes(detail.serviceZipCodes).filter((zip) => !excluded.has(zip));
}

export function getStepErrors(
  stepId: string,
  client: ClientContext,
  answers: OnboardingAnswers,
): string[] {
  if (stepId === "locations" && answers.selectedLocationIds.length === 0) {
    return ["Select at least one location that needs more sales opportunities."];
  }

  if (stepId.startsWith(STEP_PREFIX.reviewLocation)) {
    const id = stepId.slice(STEP_PREFIX.reviewLocation.length);
    const detail = answers.locationDetails[id];
    if (!detail) return ["We could not find this location’s details."];
    const errors: string[] = [];
    if (!detail.licenseNumber.trim()) errors.push("Add the license number.");
    if (!detail.marketName.trim()) errors.push("Add the market or area name.");
    if (!detail.callsCoverage) errors.push("Confirm how calls are answered.");
    if (detail.callsCoverage === "custom" && !detail.callHours.trim()) {
      errors.push("Add the hours when calls are answered.");
    }
    return errors;
  }

  if (stepId === "location-schedules" && !answers.locationScheduleMode) {
    return ["Choose whether the locations share a sales schedule."];
  }

  if (stepId === "location-budgets" && !answers.locationBudgetMode) {
    return ["Choose whether each location has its own fixed marketing budget."];
  }

  if (stepId.startsWith(STEP_PREFIX.serviceArea)) {
    const id = stepId.slice(STEP_PREFIX.serviceArea.length);
    if (parseZipCodes(answers.locationDetails[id]?.serviceZipCodes ?? "").length === 0) {
      return ["Add at least one five-digit service ZIP code."];
    }
  }

  if (stepId === "sales-role" && !answers.salesRepTitle.trim()) {
    return ["Add the title your team uses for a sales representative."];
  }

  if (
    stepId === "quality-role" &&
    !answers.noQaRole &&
    !answers.qaRoleTitle.trim()
  ) {
    return ["Add the quality-assurance role or confirm that you do not have one."];
  }

  if (stepId === "email-platform") {
    if (!answers.emailPlatform) return ["Choose your company’s email platform."];
    if (answers.emailPlatform === "other" && !answers.otherEmailPlatform.trim()) {
      return ["Tell us which email platform your company uses."];
    }
  }

  if (stepId === "fsm") {
    if (!answers.fsm) return ["Choose your field service management software."];
    if (answers.fsm === "other" && !answers.otherFsm.trim()) {
      return ["Tell us which field service management software you use."];
    }
  }

  if (stepId === "marketing-pro" && !answers.serviceTitanMarketingPro) {
    return ["Choose Yes, No, or Unsure."];
  }

  if (
    stepId === "shared-inbox" &&
    !answers.needsSharedInbox &&
    !isEmail(answers.sharedInbox)
  ) {
    return ["Add a valid shared inbox or choose “We need to create one.”"];
  }

  if (stepId === "lead-owner") {
    return personErrors(answers.primaryLeadContact, "lead responder");
  }

  if (stepId === "office-manager" && !answers.noOfficeManager) {
    return personErrors(answers.officeManager, "office manager");
  }

  if (
    stepId === "lead-notifications" &&
    answers.notificationEmails.some(
      (email) => email.trim().length > 0 && !isEmail(email),
    )
  ) {
    return ["Check the notification email addresses and remove any incomplete entries."];
  }

  if (stepId === "salespeople") {
    if (answers.salespeople.length === 0) {
      return ["Add at least one dedicated salesperson."];
    }
    return answers.salespeople.flatMap((person, index) =>
      personErrors(person, `salesperson ${index + 1}`),
    );
  }

  if (stepId === "sales-manager" && !answers.noSalesManager) {
    return personErrors(answers.salesManager, "sales manager");
  }

  if (stepId === "media-sharing" && !answers.mediaSharingMethod) {
    return ["Choose how you plan to share photos and videos."];
  }

  if (
    stepId === "meeting-prep" &&
    !Object.values(answers.meetingAcknowledgements).every(Boolean)
  ) {
    return ["Confirm all three meeting-readiness items."];
  }

  if (stepId === "review") {
    const flow = getFlow(client, { ...answers, submitted: false, meetingBooked: false });
    const incomplete = flow
      .filter((step) => !["welcome", "review"].includes(step.id))
      .flatMap((step) => getStepErrors(step.id, client, answers));
    if (incomplete.length > 0) {
      return [`There ${incomplete.length === 1 ? "is" : "are"} ${incomplete.length} incomplete ${incomplete.length === 1 ? "answer" : "answers"} to review.`];
    }
  }

  return [];
}

function personErrors(person: PersonAnswer, label: string): string[] {
  const errors: string[] = [];
  if (!person.name.trim()) errors.push(`Add the ${label}’s full name.`);
  if (!isEmail(person.email)) errors.push(`Add a valid email for the ${label}.`);
  return errors;
}

export function isEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
}
